import useModal from 'hooks/useModal';
import TodoTable from './components/TodoTable';
import TodoDrawer from './components/TodoDrawer';
import {
  FilterValue,
  HeaderFilter,
  StatusFilter,
  TypeFilter,
  filterHasValue,
} from './components/TodoFilter';
import useCrud from 'hooks/useCrud';
import { todoList, todoTypeList } from './service';
import { useGetFollowedStoreList } from 'hooks/business/useGetFollowedStoreList';
import { DownloadOutlined, LoadingOutlined, OrderedListOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Button, Checkbox, Popover, message } from 'antd';
import styled from '@emotion/styled';
import TransComponent from 'components/TransComponent';
import TypeOrder from './components/TypeOrder';
import { defaultPage, useFilterQuery } from './hooks/useFilterQuery';
import { BatchClose } from './components/BatchClose';
import { taskDetail } from './service';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import DeletedList from './components/DeletedList';
import { map, find, get, uniqBy, isUndefined } from 'lodash-es';
import { T, useTranslate } from '@tolgee/react';
import {
  BatchCloseWithResultSelfProps,
  BatchCloseWithResult,
} from './components/BatchCloseWithResult';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { exportTodoTypeDetail } from 'views/Todo/service';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { BatchTransferWithResult } from './components/BatchTransferWithResult';
import { TodoTableRef } from './components/TodoTable';

function Todo() {
  const { t } = useTranslate();

  const { whiteListConfig: { ERP } = {} } = useOrgConfigStateValue();

  const { queryFilterValue, onQueryFilterChange } = useFilterQuery();

  const { visible, show, hide, meta } = useModal<Todo.Record>();
  const { data: taskDetailData } = useRequest(() => taskDetail(queryFilterValue.taskId!), {
    refreshDeps: [queryFilterValue.taskId],
    ready: !!queryFilterValue.taskId,
  });

  const {
    item: typeList,
    loading: typeListLoading,
    fetch: fetchTypeList,
    setItem: setTypeList,
  } = useCrud({
    read: todoTypeList,
  });

  const getFollowedStoreList = useGetFollowedStoreList();

  const { item, loading, fetch } = useCrud({
    read: (filter: Util.PartialRequired<FilterValue, 'type'>) => {
      const { follower, ...others } = filter;

      return follower !== '1'
        ? todoList(others)
        : getFollowedStoreList('VISIT_TODO_MANAGEMENT_CENTER').then((res) => {
            return todoList({
              ...others,
              storeIds: res.map(({ storeId }) => storeId),
            });
          });
    },
  });

  const { type, todoStatus } = queryFilterValue;
  const customTypeLabelMap =
    taskDetailData?.policies?.reduce((acc, cur) => {
      acc[cur.type] = cur.name;
      return acc;
    }, {} as Record<string, string>) || ({} as Record<string, string>);

  const showSubType = useMemo(() => {
    if (type === -998) {
      return false;
    }
    if (type === -1 || type === -999) {
      return true;
    }
    return !!get(
      find(get(taskDetailData, 'policies'), (o) => {
        return o.type === type;
      }),
      'subTypeTrees',
    )?.length;
  }, [taskDetailData, type]);

  useEffect(() => {
    if (queryFilterValue.taskId) {
      fetchTypeList({ taskId: queryFilterValue.taskId })
        .then((res) => {
          const defaultType = res.typeOrder[0] ?? -1;

          if (type === undefined) {
            onQueryFilterChange({
              ...queryFilterValue,
              type: defaultType,
            });
          }

          return fetch({
            ...queryFilterValue,
            type: type || defaultType,
          });
        })
        .catch(() => {
          message.error(t('phrase.获取数据失败，请稍后再试'));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFilterValue.taskId]);

  const notShow = !typeList || type === undefined;

  const { todoInstances, typeCountMap, todoStatusCountMap } = item || {};

  const onFilterChange = (partial: Partial<FilterValue>) => {
    const filter = {
      ...queryFilterValue,
      ...defaultPage,
      type,
      ...partial,
    };

    onQueryFilterChange(filter);

    return fetch({
      ...filter,
      filterErpReview: [1, 2].includes(filter.type) && filter.filterErpReview,
    } as any).catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });
  };

  const headerFilter = <HeaderFilter filter={queryFilterValue} onFilterChange={onFilterChange} />;

  const statusFilter = (
    <StatusFilter
      todoStatusCountMap={todoStatusCountMap}
      filter={queryFilterValue}
      onFilterChange={onFilterChange}
    />
  );

  const typeFilter = (
    <TypeFilter
      typeList={typeList!}
      typeCountMap={typeCountMap}
      filter={queryFilterValue}
      onFilterChange={onFilterChange}
      customTypeLabelMap={customTypeLabelMap}
    />
  );

  const drawer = meta && (
    <TodoDrawer
      destroyOnClose
      todo={meta}
      visible={visible}
      onClose={() => hide()}
      onUpdate={() =>
        onFilterChange({
          pageNum: queryFilterValue.pageNum,
          pageSize: queryFilterValue.pageSize,
        })
      }
    />
  );

  const {
    visible: visibleB,
    show: showB,
    meta: metaB,
    hide: hideB,
  } = useModal<BatchCloseWithResultSelfProps>();
  const {
    visible: visibleT,
    show: showT,
    meta: metaT,
    hide: hideT,
  } = useModal<BatchCloseWithResultSelfProps>();

  const tableRef = useRef<TodoTableRef>(null);

  const table = (
    <TodoTable
      ref={tableRef}
      showSubType={showSubType}
      loading={loading}
      dataSource={todoInstances}
      onCheckDetail={show}
      type={type}
      batchOpsNodeRender={(rows) => (
        <>
          <Button
            type="link"
            onClick={() => {
              showT({
                records: rows,
              });
            }}
          >
            {t('批量转交')}
          </Button>
          <Button
            type="link"
            onClick={() => {
              showB({
                records: rows,
              });
            }}
          >
            {t('ViewShops.批量关闭')}
          </Button>
        </>
      )}
      pagination={{
        current: queryFilterValue.pageNum,
        pageSize: queryFilterValue.pageSize,
        className: 'sticky bottom-0 z-[29] bg-[#fff] !m-0 !p-[16px]',
        total:
          todoStatus === -1
            ? Object.values(todoStatusCountMap || {}).reduce((res, cur) => res + cur, 0)
            : todoStatusCountMap?.[todoStatus],
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (x) => t('feedback.共x项', { x }),
        onChange: (pageNum, pageSize) => onFilterChange({ pageNum, pageSize }),
      }}
      style={{ flex: '1 0 0', padding: '0 24px' }}
    />
  );

  const results = useMemo(() => {
    const match = find(get(taskDetailData, 'policies', []), { type });
    return map(uniqBy(get(match, 'templates'), 'resultType'), 'resultType');
  }, [type, taskDetailData]);

  const lang = useSystemLanguageValue();
  const getPermission = useGetPermission();
  const exportEnable = getPermission('导出待办数据');
  const showErpReview = getPermission('审核补发单') && ERP;
  const { runAsync: onDownload, loading: downloadLoading } = useExportFile();
  const runAsync = useCallback(() => {
    return onDownload(() => {
      return exportTodoTypeDetail({
        ...queryFilterValue,
        lang: lang,
      } as any);
    });
  }, [lang, onDownload, queryFilterValue]);

  const refresh = () => {
    if (isUndefined(type)) {
      return;
    }
    tableRef.current?.clearSelectedRows();

    fetch({
      ...queryFilterValue,
      type,
    }).catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });
  };

  return (
    <Container>
      <header>{headerFilter}</header>
      {typeListLoading ? (
        <LoadingOutlined />
      ) : notShow ? null : (
        <>
          <div>
            <div>
              {filterHasValue(queryFilterValue) && typeCountMap && (
                <T
                  keyName="todo.共查询到x条未完成的待办"
                  params={{
                    x: (
                      <TransComponent
                        content={
                          <span>
                            {Object.keys(typeCountMap).reduce<number>(
                              (res, key) => res + typeCountMap[key],
                              0,
                            )}
                          </span>
                        }
                      />
                    ),
                  }}
                />
              )}
            </div>
            <div>
              <DeletedList filterValue={queryFilterValue} />
              <BatchClose
                onRefresh={() => {
                  fetch({
                    ...queryFilterValue,
                    type,
                  }).catch(() => {
                    message.error(t('phrase.获取数据失败，请稍后再试'));
                  });
                }}
              />
              <Popover
                trigger="click"
                placement="bottomRight"
                content={
                  <TypeOrder
                    taskId={queryFilterValue.taskId!}
                    customTypeLabelMap={customTypeLabelMap}
                    typeList={typeList!}
                    onTypeListChange={(next) => {
                      if (type === -1) {
                        if (next.otherType.length === 0) {
                          onFilterChange({ type: typeList!.otherType[0] || next.typeOrder[0] });
                        } else if (next.otherType.length !== typeList!.otherType.length) {
                          onFilterChange({ type: -1 });
                        }
                      } else if (!next.typeOrder.includes(type)) {
                        onFilterChange({ type: -1 });
                      }

                      setTypeList(next);
                    }}
                  />
                }
              >
                <Button type="text" icon={<OrderedListOutlined />}>
                  {t('todo.自定义展示方案')}
                </Button>
              </Popover>
            </div>
          </div>
          <section>
            <div className="sticky top-0 z-[29] sticky-group bg-[#fff]">{typeFilter}</div>
            <div className="flex items-center justify-between py-[16px] px-[24px]">
              {statusFilter}
              <div>
                {showErpReview && [1, 2].includes(queryFilterValue.type) && (
                  <Checkbox
                    checked={queryFilterValue.filterErpReview}
                    onChange={({ target: { checked } }) =>
                      onFilterChange({ filterErpReview: checked })
                    }
                  >
                    {t('仅看需审核补发单的待办')}
                  </Checkbox>
                )}
                {exportEnable && (
                  <Button onClick={runAsync} loading={downloadLoading} icon={<DownloadOutlined />}>
                    {t('ViewDashboard.导出待办明细')}
                  </Button>
                )}
              </div>
            </div>
            {table}
            {metaT && visibleT && (
              <BatchTransferWithResult
                visible={visibleT}
                {...metaT}
                onCancel={() => {
                  hideT();
                  refresh();
                }}
              />
            )}
            {metaB && visibleB && (
              <BatchCloseWithResult
                results={results}
                visible={visibleB}
                {...metaB}
                onCancel={() => {
                  hideB();
                  refresh();
                }}
              />
            )}
          </section>
          {drawer}
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 86px);
  overflow-y: auto;
  > * {
    flex: 0 0 auto;
  }
  > header {
    padding: 16px 24px;
    background: #fff;
  }
  > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 24px;
    div {
      color: #84868c;
      > span {
        padding: 0 4px;
        color: var(--ant-primary-color);
      }
    }
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    background: #fff;
    > * {
      flex: 0 0 auto;
    }
  }
`;

export default Todo;
